import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShieldCheckIcon, SparklesIcon, PaperclipIcon, SendIcon, MoreVerticalIcon, CheckIcon, CheckCheckIcon, ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
type Presence = {
  state: 'online' | 'typing' | 'offline';
  lastSeen?: string;
};
type MsgStatus = 'sent' | 'delivered' | 'read';
interface Message {
  id: number;
  fromMe: boolean;
  text: string;
  time: string;
  status?: MsgStatus;
}
interface ListingInfo {
  title: string;
  price: string;
  image: string;
  specs: {
    label: string;
    value: string;
  }[];
}
interface Conversation {
  id: string;
  name: string;
  type: string;
  lastMessage: string;
  time: string;
  unread: number;
  avatar: string;
  listing: string | null;
  listingInfo?: ListingInfo;
  presence: Presence;
}
const mockConversations: Conversation[] = [{
  id: '1',
  name: 'Al Futtaim Automall',
  type: 'Verified Business',
  lastMessage: 'Yes, the car is available for viewing today.',
  time: '10:42 AM',
  unread: 2,
  avatar: 'A',
  listing: 'Toyota Prado GXR 2018',
  listingInfo: {
    title: 'Toyota Prado GXR 2018',
    price: 'AED 95,000',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=200&q=80',
    specs: [{
      label: 'Mileage',
      value: '78,000 km'
    }, {
      label: 'Transmission',
      value: 'Automatic'
    }, {
      label: 'Fuel',
      value: 'Petrol'
    }, {
      label: 'Location',
      value: 'Dubai, Al Quoz'
    }]
  },
  presence: {
    state: 'online'
  }
}, {
  id: '2',
  name: 'Talab AI Assistant',
  type: 'AI Agent',
  lastMessage: "I've found 3 new matches for your villa request.",
  time: 'Yesterday',
  unread: 0,
  avatar: 'T',
  listing: null,
  presence: {
    state: 'online'
  }
}, {
  id: '3',
  name: 'Ahmed M.',
  type: 'Individual',
  lastMessage: 'Can we negotiate the price?',
  time: 'Mon',
  unread: 0,
  avatar: 'AM',
  listing: 'MacBook Pro M2',
  listingInfo: {
    title: 'MacBook Pro M2 14"',
    price: 'AED 6,500',
    image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=200&q=80',
    specs: [{
      label: 'Storage',
      value: '512 GB SSD'
    }, {
      label: 'Memory',
      value: '16 GB'
    }, {
      label: 'Condition',
      value: 'Like new'
    }, {
      label: 'Warranty',
      value: '6 months left'
    }]
  },
  presence: {
    state: 'offline',
    lastSeen: 'today at 9:14 AM'
  }
}];
const initialThreads: Record<string, Message[]> = {
  '1': [{
    id: 1,
    fromMe: true,
    text: 'Hi, is the car still available?',
    time: '10:38 AM',
    status: 'read'
  }, {
    id: 2,
    fromMe: false,
    text: 'Yes, the car is available for viewing today. We are open until 8 PM.',
    time: '10:40 AM'
  }, {
    id: 3,
    fromMe: true,
    text: 'Great! Can I come by around 5 PM?',
    time: '10:41 AM',
    status: 'read'
  }, {
    id: 4,
    fromMe: false,
    text: 'Yes, the car is available for viewing today.',
    time: '10:42 AM'
  }],
  '2': [{
    id: 1,
    fromMe: false,
    text: "I've found 3 new matches for your villa request.",
    time: 'Yesterday'
  }],
  '3': [{
    id: 1,
    fromMe: false,
    text: 'Can we negotiate the price?',
    time: 'Mon'
  }, {
    id: 2,
    fromMe: true,
    text: "Let's discuss — what did you have in mind?",
    time: 'Mon',
    status: 'delivered'
  }]
};
function presenceLabel(p: Presence) {
  if (p.state === 'online') return {
    text: 'online',
    cls: 'text-green-600'
  };
  if (p.state === 'typing') return {
    text: 'typing…',
    cls: 'text-primary-indigo'
  };
  return {
    text: `last seen ${p.lastSeen ?? 'recently'}`,
    cls: 'text-slate-400'
  };
}
function Ticks({
  status
}: {
  status?: MsgStatus;
}) {
  if (!status) return null;
  if (status === 'sent') return <CheckIcon className="w-3.5 h-3.5 text-white/60" data-id="element-2843" />;
  if (status === 'delivered') return <CheckCheckIcon className="w-3.5 h-3.5 text-white/60" data-id="element-2844" />;
  return <CheckCheckIcon className="w-3.5 h-3.5 text-sky-300" data-id="element-2845" />;
}
export function Messages() {
  const [activeChat, setActiveChat] = useState<Conversation>(mockConversations[0]);
  // On tablet/mobile we show either the inbox list OR the open chat (master-detail).
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [listingExpanded, setListingExpanded] = useState(false);
  const [threads, setThreads] = useState<Record<string, Message[]>>(initialThreads);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = threads[activeChat.id] ?? [];
  const presence = presenceLabel(activeChat.presence);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight
    });
  }, [messages.length, activeChat.id]);
  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const id = Date.now();
    const time = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
    setThreads(prev => ({
      ...prev,
      [activeChat.id]: [...(prev[activeChat.id] ?? []), {
        id,
        fromMe: true,
        text,
        time,
        status: 'sent'
      }]
    }));
    setDraft('');
    // Simulate WhatsApp-style delivery → read progression.
    const update = (status: MsgStatus) => setThreads(prev => ({
      ...prev,
      [activeChat.id]: (prev[activeChat.id] ?? []).map(m => m.id === id ? {
        ...m,
        status
      } : m)
    }));
    window.setTimeout(() => update('delivered'), 700);
    window.setTimeout(() => update('read'), 1800);
  };
  return <div className="flex-1 flex h-[calc(100vh-57px)] lg:h-[calc(100vh-80px)] bg-white overflow-hidden" data-id="element-2846">
      {/* Inbox List (Left) */}
      <div className={`${mobileView === 'chat' ? 'hidden' : 'flex'} w-full lg:flex lg:w-80 border-r border-slate-200 flex-col bg-slate-50/50`} data-id="element-2847">
        <div className="p-4 border-b border-slate-200 bg-white" data-id="element-2848">
          <h1 className="font-display font-bold text-2xl text-dark-navy mb-4" data-id="element-2849">
            Messages
          </h1>
          <div className="relative" data-id="element-2850">
            <SearchIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2851" />
            <input type="text" placeholder="Search messages..." className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-transparent rounded-xl text-sm focus:bg-white focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/10 transition-all" data-id="element-2852" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-2853">
          {mockConversations.map(chat => {
          const cp = presenceLabel(chat.presence);
          return <button key={chat.id} onClick={() => {
            setActiveChat(chat);
            setMobileView('chat');
            setListingExpanded(false);
          }} className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors flex gap-3 ${activeChat.id === chat.id ? 'bg-primary-indigo/5 border-l-4 border-l-primary-indigo' : 'border-l-4 border-l-transparent'}`} data-id="element-2854">
                <div className="relative flex-shrink-0" data-id="element-2855">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${chat.type === 'AI Agent' ? 'bg-gradient-to-br from-primary-indigo to-ai-purple' : 'bg-slate-300'}`} data-id="element-2856">
                    {chat.type === 'AI Agent' ? <SparklesIcon className="w-6 h-6" data-id="element-2857" /> : chat.avatar}
                  </div>
                  {chat.presence.state === 'online' && <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" data-id="element-2858" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-2859">
                  <div className="flex justify-between items-baseline mb-1" data-id="element-2860">
                    <h3 className="font-bold text-slate-900 truncate pr-2" data-id="element-2861">
                      {chat.name}
                    </h3>
                    <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-2862">
                      {chat.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs mb-1" data-id="element-2863">
                    {chat.type === 'Verified Business' && <ShieldCheckIcon className="w-3.5 h-3.5 text-success" data-id="element-2864" />}
                    <span className={chat.type === 'AI Agent' ? 'text-ai-purple font-medium' : 'text-slate-500'} data-id="element-2865">
                      {chat.type}
                    </span>
                  </div>
                  <p className={`text-sm truncate ${chat.unread > 0 ? 'font-semibold text-slate-900' : 'text-slate-500'}`} data-id="element-2866">
                    {chat.lastMessage}
                  </p>
                </div>
                {chat.unread > 0 && <div className="w-5 h-5 bg-primary-indigo text-white text-xs font-bold flex items-center justify-center rounded-full mt-1 flex-shrink-0" data-id="element-2867">
                    {chat.unread}
                  </div>}
              </button>;
        })}
        </div>
      </div>

      {/* Conversation Panel (Right) */}
      <div className={`${mobileView === 'chat' ? 'flex' : 'hidden'} lg:flex flex-1 flex-col bg-white min-w-0`} data-id="element-2868">
        {/* Chat Header */}
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-2869">
          <div className="flex items-center gap-3" data-id="element-2870">
            <button onClick={() => setMobileView('list')} aria-label="Back to messages" className="lg:hidden -ml-1 p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0" data-id="element-2871">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-2872" />
            </button>
            <div className="relative" data-id="element-2873">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${activeChat.type === 'AI Agent' ? 'bg-gradient-to-br from-primary-indigo to-ai-purple' : 'bg-slate-300'}`} data-id="element-2874">
                {activeChat.type === 'AI Agent' ? <SparklesIcon className="w-5 h-5" data-id="element-2875" /> : activeChat.avatar}
              </div>
              {activeChat.presence.state === 'online' && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-2876" />}
            </div>
            <div data-id="element-2877">
              <div className="flex items-center gap-1.5" data-id="element-2878">
                <h2 className="font-bold text-slate-900" data-id="element-2879">{activeChat.name}</h2>
                {activeChat.type === 'Verified Business' && <ShieldCheckIcon className="w-4 h-4 text-success" data-id="element-2880" />}
              </div>
              <span className={`text-xs font-medium ${presence.cls}`} data-id="element-2881">
                {presence.text}
              </span>
            </div>
          </div>
          <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors" data-id="element-2882">
            <MoreVerticalIcon className="w-5 h-5" data-id="element-2883" />
          </button>
        </div>

        {/* Product banner (if listing attached) — expandable to reveal details */}
        {activeChat.listingInfo && <div className="bg-slate-50 border-b border-slate-200" data-id="element-2884">
            <button onClick={() => setListingExpanded(v => !v)} aria-expanded={listingExpanded} className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-100/70 transition-colors" data-id="element-2885">
              <img src={activeChat.listingInfo.image} alt={activeChat.listingInfo.title} className="w-11 h-11 rounded-lg object-cover flex-shrink-0 border border-slate-200" data-id="element-2886" />
              <div className="flex-1 min-w-0" data-id="element-2887">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium" data-id="element-2888">
                  Regarding
                </p>
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-2889">
                  {activeChat.listingInfo.title}
                </p>
              </div>
              <span className="text-sm font-bold text-primary-indigo whitespace-nowrap flex-shrink-0" data-id="element-2890">
                {activeChat.listingInfo.price}
              </span>
              <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${listingExpanded ? 'rotate-180' : ''}`} data-id="element-2891" />
            </button>
            <AnimatePresence initial={false} data-id="element-2892">
              {listingExpanded && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="overflow-hidden" data-id="element-2893">
                  <div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-2894">
                    {activeChat.listingInfo.specs.map(s => <div key={s.label} className="flex justify-between items-baseline border-b border-slate-200/70 pb-1.5" data-id="element-2895">
                        <span className="text-xs text-slate-500" data-id="element-2896">
                          {s.label}
                        </span>
                        <span className="text-xs font-medium text-slate-800" data-id="element-2897">
                          {s.value}
                        </span>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        {/* Chat Messages Area */}
        <div ref={scrollRef} className="flex-1 px-4 py-6 overflow-y-auto bg-slate-50/60" data-id="element-2898">
          <div className="flex justify-center mb-6" data-id="element-2899">
            <span className="text-xs font-medium text-slate-500 bg-white border border-slate-200 px-3 py-1 rounded-full shadow-sm" data-id="element-2900">
              Today
            </span>
          </div>
          <div className="space-y-2" data-id="element-2901">
            <AnimatePresence initial={false} data-id="element-2902">
              {messages.map(m => <motion.div key={m.id} layout initial={{
              opacity: 0,
              y: 8,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 28
            }} className={`flex ${m.fromMe ? 'justify-end' : 'justify-start'}`} data-id="element-2903">
                  {m.fromMe ? <div className="bg-primary-indigo text-white rounded-2xl rounded-br-md px-3.5 py-2 max-w-[70%] shadow-sm" data-id="element-2904">
                      <p className="text-sm leading-relaxed" data-id="element-2905">{m.text}</p>
                      <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5" data-id="element-2906">
                        <span className="text-[10px] text-white/70" data-id="element-2907">
                          {m.time}
                        </span>
                        <Ticks status={m.status} data-id="element-2908" />
                      </div>
                    </div> : <div className="bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2 max-w-[70%] shadow-sm" data-id="element-2909">
                      <p className="text-sm leading-relaxed" data-id="element-2910">{m.text}</p>
                      <div className="flex justify-end mt-0.5 -mb-0.5" data-id="element-2911">
                        <span className="text-[10px] text-slate-400" data-id="element-2912">
                          {m.time}
                        </span>
                      </div>
                    </div>}
                </motion.div>)}
            </AnimatePresence>
          </div>
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-slate-200" data-id="element-2913">
          <div className="flex items-center gap-2 bg-slate-100 rounded-2xl px-2 py-1.5 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-indigo/15 border border-transparent focus-within:border-primary-indigo/30 transition-all" data-id="element-2914">
            <button className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-white rounded-xl transition-colors flex-shrink-0" data-id="element-2915">
              <PaperclipIcon className="w-5 h-5" data-id="element-2916" />
            </button>
            <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }} placeholder="Type a message..." className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400 py-2" data-id="element-2917" />
            <button onClick={send} disabled={!draft.trim()} className="p-2.5 bg-primary-indigo text-white rounded-xl hover:bg-primary-indigo/90 transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2918">
              <SendIcon className="w-5 h-5" data-id="element-2919" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}